import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { css, cx } from '@linaria/atomic';
import composeRefs from '@seznam/compose-react-refs';
import { useInView } from 'react-intersection-observer';
import { CallIntelCallInsightsTrialTeaser } from '@frontend/call-intel-assets';
import { useTranslation } from '@frontend/i18n';
import { PanelFeaturePromotionBanner } from '@frontend/plg-banners';
import { WeaveUpgradeIcon } from '@frontend/self-serve#svg';
import { Button } from '@frontend/design-system';
import { useCallInsightTrialTeaser } from '../../hooks/use-call-insight-trial-teaser';
import { getCallIntelTrialTeaserTrackingIds } from '../../tracking-ids';
import { CallIntelTrialTeaserAction } from './call-intel-trial-teaser-action';

export interface CallIntelTrialTeaserPromotionProps {
  locationId: string;
  isDemo?: boolean;
  className?: string;
  innerRef?: React.Ref<HTMLDivElement>;
  onDismiss?: () => void;
  onStartFreeTrial?: () => void;
  onViewDemo?: () => void;
}

const UPGRADE_CLICK_SOURCE = 'ci-insights-trial-teaser';

export const CallIntelTrialTeaserPromotion: FC<CallIntelTrialTeaserPromotionProps> = ({
  locationId,
  isDemo = false,
  className,
  innerRef,
  onDismiss,
  onStartFreeTrial,
  onViewDemo,
}) => {
  const { t } = useTranslation('call-intelligence');

  const {
    isEligible,
    isAdmin,
    onDismiss: hookDismiss,
    registerTeaserViewed,
  } = useCallInsightTrialTeaser({
    locationId,
    isDemo,
  });

  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  const trackingIds = useMemo(() => getCallIntelTrialTeaserTrackingIds(isAdmin), [isAdmin]);

  useEffect(() => {
    if (inView && isEligible) {
      registerTeaserViewed();
    }
  }, [inView, isEligible, registerTeaserViewed]);

  const handleDismiss = useCallback(() => {
    hookDismiss();
    onDismiss?.();
  }, [hookDismiss, onDismiss]);

  if (!isEligible) {
    return null;
  }

  return (
    <PanelFeaturePromotionBanner
      accent='seaweed'
      innerRef={composeRefs(ref, innerRef) as React.Ref<HTMLDivElement>}
      className={cx(styles.root, className)}
      testId='call-intel-trial-teaser-promotion'
    >
      <PanelFeaturePromotionBanner.Header
        leading={<WeaveUpgradeIcon className={styles.upgradeIcon} aria-hidden='true' />}
        headingLevel={3}
        testId='trial-teaser-header'
      >
        {t('Call Intelligence Free for 30 Days')}
      </PanelFeaturePromotionBanner.Header>

      <PanelFeaturePromotionBanner.Body testId='trial-teaser-body'>
        <div>
          <PanelFeaturePromotionBanner.Title as='span' testId='trial-teaser-title'>
            {t('Get AI-powered insights for every call.')}{' '}
          </PanelFeaturePromotionBanner.Title>
          <PanelFeaturePromotionBanner.Description as='span' testId='trial-teaser-description'>
            {t('Turn each conversation into an opportunity and ensure nothing falls through the cracks.')}
          </PanelFeaturePromotionBanner.Description>
        </div>

        <PanelFeaturePromotionBanner.FeatureChips testId='trial-teaser-feature-chips'>
          <PanelFeaturePromotionBanner.FeatureChip testId='trial-teaser-chip-summary'>
            {t('Summary & transcript')}
          </PanelFeaturePromotionBanner.FeatureChip>
          <PanelFeaturePromotionBanner.FeatureChip testId='trial-teaser-chip-sentiment'>
            {t('Sentiment tracking')}
          </PanelFeaturePromotionBanner.FeatureChip>
          <PanelFeaturePromotionBanner.FeatureChip testId='trial-teaser-chip-scheduling'>
            {t('Missed scheduling detection')}
          </PanelFeaturePromotionBanner.FeatureChip>
          <PanelFeaturePromotionBanner.FeatureChip testId='trial-teaser-chip-tasks'>
            {t('Follow-up tasks')}
          </PanelFeaturePromotionBanner.FeatureChip>
        </PanelFeaturePromotionBanner.FeatureChips>

        <PanelFeaturePromotionBanner.Media testId='trial-teaser-media' noBackground>
          <CallIntelCallInsightsTrialTeaser width='100%' aria-label={t('Call Intelligence preview')} />
        </PanelFeaturePromotionBanner.Media>

        <PanelFeaturePromotionBanner.Highlights separator='bullet' testId='trial-teaser-highlights'>
          <PanelFeaturePromotionBanner.Highlight icon='credit-card-small' testId='trial-teaser-highlight-card'>
            {t('No card required')}
          </PanelFeaturePromotionBanner.Highlight>
          <PanelFeaturePromotionBanner.Highlight icon='award-small' testId='trial-teaser-highlight-subscription'>
            {t('No auto-subscription')}
          </PanelFeaturePromotionBanner.Highlight>
        </PanelFeaturePromotionBanner.Highlights>

        <PanelFeaturePromotionBanner.Actions
          testId='trial-teaser-actions'
          className={styles.actionBar}
          dismiss={
            <Button
              variant='tertiary'
              className={styles.maybeLaterButton}
              onClick={handleDismiss}
              trackingId={trackingIds.maybeLater}
              data-testid='trial-teaser-maybe-later-button'
            >
              {t('Maybe Later')}
            </Button>
          }
        >
          <CallIntelTrialTeaserAction
            locationId={locationId}
            source={UPGRADE_CLICK_SOURCE}
            testIdPrefix='trial-teaser'
            onStartFreeTrial={onStartFreeTrial}
            isAdmin={isAdmin}
          />

          <Button
            variant='secondary'
            onClick={onViewDemo}
            trackingId={trackingIds.viewDemo}
            data-testid='trial-teaser-view-demo-button'
          >
            {t('See It In Action')}
          </Button>
        </PanelFeaturePromotionBanner.Actions>
      </PanelFeaturePromotionBanner.Body>
    </PanelFeaturePromotionBanner>
  );
};

const styles = {
  root: css`
    flex-shrink: 0;
    box-shadow: none;
  `,
  upgradeIcon: css`
    width: 20px;
    height: 20px;
    scale: 2;
    transform: translateY(2px);
    flex-shrink: 0;
  `,
  actionBar: css`
    gap: var(--fabric-space-gap-xxs);
  `,
  maybeLaterButton: css`
    color: var(--fabric-color-text-secondary);

    &:hover {
      color: var(--fabric-color-text-primary);
    }
  `,
};
